import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Event } from '../types';
import { publicApi } from '../services/api';

const EventRegistration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadEvent(id);
    }
  }, [id]);

  const loadEvent = async (eventId: string) => {
    try {
      const eventData = await publicApi.getEvent(eventId);
      setEvent(eventData);
    } catch (err) {
      setError('Event not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !name.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      await publicApi.registerGuest(id, { name: name.trim() });
      navigate(`/event/${id}`);
    } catch (err) {
      setError('Failed to register. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading event...</div>;
  }

  if (error && !event) {
    return <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>{error}</div>;
  }

  if (!event) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Event not found</div>;
  }

  return (
    <div style={{ maxWidth: '600px', margin: '100px auto', padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        {event.imageUrl && (
          <img
            src={event.imageUrl}
            alt={event.title}
            style={{
              width: '120px',
              height: '120px',
              objectFit: 'cover',
              borderRadius: '50%',
              marginBottom: '20px'
            }}
          />
        )}
        <h1 style={{ margin: '0 0 10px 0' }}>You're invited!</h1>
        <h2 style={{ margin: '0 0 10px 0', color: '#333' }}>{event.title}</h2>
        <p style={{ fontSize: '16px', color: '#666', margin: '0' }}>📍 {event.location}</p>
      </div>

      {event.description && (
        <div style={{ marginBottom: '30px', textAlign: 'center' }}>
          <p style={{ lineHeight: '1.6', color: '#555' }}>{event.description}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="name" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Enter your name to join:
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Your full name"
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {error && (
          <div style={{ color: 'red', marginBottom: '20px', textAlign: 'center' }}>{error}</div>
        )}

        <button
          type="submit"
          disabled={submitting || !name.trim()}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: submitting || !name.trim() ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: submitting || !name.trim() ? 'not-allowed' : 'pointer'
          }}
        >
          {submitting ? 'Joining...' : 'Join Event'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <button
          onClick={() => navigate(`/event/${id}`)}
          style={{
            background: 'none',
            border: 'none',
            color: '#007bff',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          View event details without joining
        </button>
      </div>
    </div>
  );
};

export default EventRegistration;